/**
 * Row selection state management hook for DataList.
 *
 * Manages a set of selected rows using an internal Map keyed by row identity.
 * Row identity is resolved from `row.id` when present, falling back to
 * `JSON.stringify(row)` for rows without an explicit identifier.
 *
 * The hook deliberately does NOT compute `isAllSelected` — that is left to
 * the orchestrating `useDataList` hook which has access to the current page
 * rows at all times.
 */

import { useState, useCallback, useMemo } from 'react';
import { safeString } from '../utils/cellRenderer';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive a stable string identity for a row.
 *
 * - Uses `row.id` (coerced to string) when the field is present and non-null.
 * - Falls back to `JSON.stringify(row)` otherwise.
 */
export function getRowId(row: Record<string, unknown>): string {
  if (row.id !== undefined && row.id !== null) return safeString(row.id);
  return JSON.stringify(row);
}

// ---------------------------------------------------------------------------
// Public return type
// ---------------------------------------------------------------------------

export interface UseListSelectionReturn {
  /** Array of currently selected row objects. */
  selectedRows: Record<string, unknown>[];

  /** Toggle selection state for a single row. */
  toggleRowSelection: (row: Record<string, unknown>) => void;

  /**
   * Toggle select-all for the provided page rows.
   *
   * - If every row in `currentPageRows` is already selected, deselects them.
   * - Otherwise adds all `currentPageRows` to the selection.
   * - Selections from other pages are preserved in both cases.
   */
  toggleSelectAll: (currentPageRows: Record<string, unknown>[]) => void;

  /** Clear the entire selection. */
  clearSelection: () => void;

  /** Check whether a specific row is currently selected. */
  isRowSelected: (row: Record<string, unknown>) => boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useListSelection(): UseListSelectionReturn {
  const [selectionMap, setSelectionMap] = useState<Map<string, Record<string, unknown>>>(
    () => new Map(),
  );

  // ---- Actions ----

  const toggleRowSelection = useCallback((row: Record<string, unknown>) => {
    const id = getRowId(row);
    setSelectionMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, row);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(
    (currentPageRows: Record<string, unknown>[]) => {
      setSelectionMap((prev) => {
        const next = new Map(prev);

        const allSelected =
          currentPageRows.length > 0 &&
          currentPageRows.every((row) => next.has(getRowId(row)));

        if (allSelected) {
          // Deselect current page rows only
          for (const row of currentPageRows) {
            next.delete(getRowId(row));
          }
        } else {
          // Add all current page rows to selection
          for (const row of currentPageRows) {
            next.set(getRowId(row), row);
          }
        }

        return next;
      });
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelectionMap(new Map());
  }, []);

  const isRowSelected = useCallback(
    (row: Record<string, unknown>): boolean => selectionMap.has(getRowId(row)),
    [selectionMap],
  );

  // ---- Derived values ----

  const selectedRows = useMemo(
    () => Array.from(selectionMap.values()),
    [selectionMap],
  );

  // ---- Return ----

  return useMemo(
    () => ({
      selectedRows,
      toggleRowSelection,
      toggleSelectAll,
      clearSelection,
      isRowSelected,
    }),
    [selectedRows, toggleRowSelection, toggleSelectAll, clearSelection, isRowSelected],
  );
}
